import type { VersionResponse } from '@platform/contracts';

export interface VersionBadgeProps {
  /** Build identity of the SPA itself (Vite `define`, see scripts/version.mjs). */
  app: VersionResponse;
  /** Build identity reported by GET /version, when it has been fetched. */
  api?: VersionResponse | undefined;
  className?: string;
}

function short(info: VersionResponse): string {
  return info.commit && info.commit !== 'unknown'
    ? `${info.version} (${info.commit})`
    : info.version;
}

/**
 * Non-intrusive build identity for the app footer (rules §12.7, ARCHITECTURE.md §18).
 * Shows the API build too when it differs, which is how a half-finished deploy is spotted.
 */
export function VersionBadge({ app, api, className }: VersionBadgeProps) {
  const mismatch = api !== undefined && api.version !== app.version;
  const title = api
    ? `app ${short(app)} built ${app.builtAt} | api ${short(api)} built ${api.builtAt}`
    : `app ${short(app)} built ${app.builtAt}`;

  return (
    <span
      className={['text-xs text-neutral-400-text', className].filter(Boolean).join(' ')}
      title={title}
      data-testid="version-badge"
    >
      v{short(app)}
      {mismatch ? ` · api v${short(api)}` : null}
    </span>
  );
}
